import { z } from "zod";
import { timestampToDateNullable } from "@/core/firebase/converters";
import { type ClubStatus, type BillingStatus } from "@/core/models/club-status";

/**
 * Subscription state for a club. Stored as a nested `subscription` map inside
 * the `clubs/{clubId}` document. Stripe is the source of truth for billing —
 * these fields mirror Stripe state via Cloud Function webhooks.
 */
// SubscriptionPlan values: 'monthly' | 'annual'. Unknown / missing values
// default to 'monthly'.
export const SUBSCRIPTION_PLAN = ["monthly", "annual"] as const;
export const SubscriptionPlanSchema = z.preprocess((value) => {
  if (value === "annual") return "annual";
  return "monthly";
}, z.enum(SUBSCRIPTION_PLAN));
export type SubscriptionPlan = z.infer<typeof SubscriptionPlanSchema>;

/**
 * Pricing constants — single source of truth. Updated 2026-04-12.
 */
export const SubscriptionPricing = {
  /** Regular monthly price per court (no commitment). */
  monthlyPerCourt: 20,
  /** Annual price per court per month (billed full year upfront). Headline. */
  annualPerCourtPerMonth: 15,
  /** Annual price per court (one payment). */
  annualPerCourt: 15 * 12,
  /** Partner Club monthly price per court (permanent founder rate). */
  partnerPerCourt: 10,
  /** Lifetime cap on founder-rate clubs. `null` = no fixed number: the
   * founder program is private and invitation-only, so we don't commit to
   * a specific count. Set to an int to re-arm the numeric gate in the
   * `enforce_founder_club_cap` CF (mirror the value in `founder_cap.py`). */
  founderClubsCap: null as number | null,
  /** Trial duration — uniform 90 days for every club, partner or regular. */
  trialDaysRegular: 90,
  /** Days suspended before eligible for admin review/cleanup. */
  suspendedRetentionDays: 90,
  /** Preview token validity in days. */
  previewTokenExpiryDays: 30,
  /** Spanish IVA rate. Applied automatically by Stripe Tax when enabled. */
  ivaRate: 0.21,
} as const;

/**
 * MODULAR pricing model — the DECIDED target (Decision #12, 2026-07-17):
 * €10/court base + €20/module flat (POS · Torneos · Entrenadores) + a €50 cap
 * for all three; founders get modules free. **Additive foundation only — NOT
 * yet wired into billing.** The flat `SubscriptionPricing` above is what the
 * CFs/Stripe still charge (paywall OFF until ~club #11). This block +
 * `modularMonthlyTotal()` are the reviewable, tested basis the sandbox-tested
 * billing rewiring will build on. Full plan + Stripe objects + CF changes:
 * `apps/clubmanager-pwa/docs/stripe-modular-billing-plan.md`.
 */
export const ModularPricing = {
  /** Base per court/month for EVERY club (the booking núcleo). */
  basePerCourt: 10,
  /** Flat per-module/month (à-la-carte). */
  perModule: 20,
  /** All-modules cap: the 3 paid modules for a flat €50 (à-la-carte sum = €60). */
  moduleBundle: 50,
} as const;

/** The 3 PAID modules. Monedero+Bonos is a FREE config toggle inside CRM, not a module. */
export const PAID_MODULE_KEYS = ["pos", "tournaments", "coach"] as const;
export type PaidModuleKey = (typeof PAID_MODULE_KEYS)[number];

/**
 * Modular monthly total (net of IVA) — the recurring per-month rate,
 * identical regardless of billing cadence (Decision #12-annual,
 * 2026-08-15): flat €10/court for everyone. `activeModuleCount` = how many
 * of the 3 paid modules the club has active; the €50 bundle caps all
 * three. Founders pay the base only (modules free, forever).
 */
export function modularMonthlyTotal(
  courtCount: number,
  activeModuleCount: number,
  opts: { founder?: boolean } = {},
): { base: number; modules: number; total: number } {
  const base = Math.max(0, courtCount) * ModularPricing.basePerCourt;
  const n = Math.max(0, Math.min(activeModuleCount, PAID_MODULE_KEYS.length));
  const modules = opts.founder
    ? 0
    : n >= PAID_MODULE_KEYS.length
      ? ModularPricing.moduleBundle
      : n * ModularPricing.perModule;
  return { base, modules, total: base + modules };
}

export const SubscriptionSchema = z.object({
  // NOTE: lifecycle `status` and billing `billingStatus` live TOP-LEVEL on the
  // club doc, not here. The `subscription` map holds only Stripe metadata
  // (plan, dates, ids, card). Helpers below that need the lifecycle/billing
  // state take it as an explicit param — `subscription.ts` can't import `Club`
  // (circular) so it can't read it off the club.
  plan: SubscriptionPlanSchema.default("monthly"),
  partnerClub: z.boolean().default(false),
  /** Contractual signed date required by `enforce_founder_club_cap` CF when flipping partnerClub → true. */
  partnerAgreementSignedAt: timestampToDateNullable.nullable().optional(),
  trialStartDate: timestampToDateNullable.nullable().optional(),
  trialEndDate: timestampToDateNullable.nullable().optional(),
  /**
   * Read-only-to-closure deadline, set by hand by a platform admin (Decision
   * #16) — never auto-computed. Unset (the default for every club) means
   * read-only access continues indefinitely with no closure scheduled; no
   * automated job runs off this today, closure stays a manual admin action
   * either way. Writable only by `isAdmin()` — the club-owner branch of
   * firestore.rules already restricts `subscription.*` writes to `plan`
   * alone, so this needs no separate rule.
   */
  graceEndDate: timestampToDateNullable.nullable().optional(),
  currentPeriodStart: timestampToDateNullable.nullable().optional(),
  currentPeriodEnd: timestampToDateNullable.nullable().optional(),
  stripeCustomerId: z.string().nullable().optional(),
  stripeSubscriptionId: z.string().nullable().optional(),
  stripePriceId: z.string().nullable().optional(),
  paymentMethodAdded: z.boolean().default(false),
  /**
   * Card preview fields — last 4 digits, brand, and expiry. Synced from
   * Stripe by the finalise CF (on first attach + on "Cambiar tarjeta")
   * and by the subscription.updated webhook (covers card changes made via
   * the Customer Portal). Optional because the club may not have added a
   * card yet; shown as a fallback "Tarjeta guardada" when missing.
   *
   * Non-PCI safe-to-display per Stripe's docs:
   * https://stripe.com/docs/api/payment_methods/object#payment_method_object-card
   */
  paymentMethodLast4: z.string().nullable().optional(),
  paymentMethodBrand: z.string().nullable().optional(),
  paymentMethodExpMonth: z.number().int().min(1).max(12).nullable().optional(),
  paymentMethodExpYear: z.number().int().nullable().optional(),
  cancelAtPeriodEnd: z.boolean().default(false),
  lastPaymentDate: timestampToDateNullable.nullable().optional(),
  lastPaymentAmount: z.number().nullable().optional(),
  nextInvoiceDate: timestampToDateNullable.nullable().optional(),
  nextInvoiceAmount: z.number().nullable().optional(),
  deletionRequestedAt: timestampToDateNullable.nullable().optional(),
  deletionRequestedBy: z.string().nullable().optional(),
  previousStatus: z.string().nullable().optional(),
  /**
   * True when Stripe Tax is engaged for this subscription AND the latest
   * tax computation completed successfully. Drives the in-app IVA
   * breakdown display — when false (pre-launch / dashboard toggle off /
   * tax computation failed), prices render net only.
   * Source: webhook payload's `subscription.automatic_tax.{enabled,status}`,
   * mirrored by `_serialize_subscription_for_firestore` in functions/main.py.
   */
  automaticTaxEnabled: z.boolean().default(false),
  /**
   * Modular billing (Decision #12) — DARK until the ~club #11 enforcement flip.
   * `modularBillingEnforced`: opt-in switch (per-club for sandbox testing now,
   * a global later). While false, the club bills the legacy single flat/base
   * line — no module charges. `modularBillingActive`: reflected state — true once
   * the Stripe subscription actually carries base + module/bundle lines. Both
   * written by `_serialize_subscription_for_firestore` in functions/main.py.
   */
  modularBillingEnforced: z.boolean().optional(),
  modularBillingActive: z.boolean().optional(),
  /**
   * True when this trial was started by `convert_mockup_to_trial` (i.e.
   * the owner claimed an admin-built mockup). Read live by
   * `subscription-card.tsx` to gate the one-shot `viewedAt` stamp for the
   * post-claim tutorial's "Conoce tu prueba" step — only mockup-claim
   * trials run that tutorial, so self-created clubs never get the field
   * stamped.
   */
  startedFromMockup: z.boolean().default(false),
  /**
   * First time the current owner opened `/settings?tab=subscription`. Used
   * to auto-complete the "Conoce tu prueba" step of the post-claim tutorial.
   * Set client-side on first visit; never overwritten.
   */
  viewedAt: timestampToDateNullable.nullable().optional(),
});

export type Subscription = z.infer<typeof SubscriptionSchema>;
// ─── Derived helpers ────────────────────────────────────────────────────────

export function isSubscriptionTrialActive(
  s: Subscription,
  status: ClubStatus,
  now: Date = new Date(),
): boolean {
  return status === "trial" && s.trialEndDate != null && now < s.trialEndDate;
}

export function isSubscriptionTrialExpired(
  s: Subscription,
  status: ClubStatus,
  now: Date = new Date(),
): boolean {
  return status === "trial" && s.trialEndDate != null && now > s.trialEndDate;
}

/** Days remaining in the trial, clamped to [0, 999]. */
export function subscriptionTrialDaysRemaining(
  s: Subscription,
  now: Date = new Date(),
): number {
  if (s.trialEndDate == null) return 0;
  const diffMs = s.trialEndDate.getTime() - now.getTime();
  const days = Math.floor(diffMs / 86_400_000);
  if (days < 0) return 0;
  if (days > 999) return 999;
  return days;
}

/** Monthly price per court based on plan and partner status. */
export function monthlyPricePerCourt(s: Subscription): number {
  if (s.partnerClub) return SubscriptionPricing.partnerPerCourt;
  if (s.plan === "annual") return SubscriptionPricing.annualPerCourtPerMonth;
  return SubscriptionPricing.monthlyPerCourt;
}

/**
 * Modular per-court rate (Decision #12) — the `monthlyPricePerCourt`
 * equivalent for the modular model. Court-only: does not include module
 * fees, which are flat per club rather than per-court (see
 * `modularMonthlyTotal` for the base+modules total).
 *
 * Always `ModularPricing.basePerCourt` now (Decision #12-annual,
 * 2026-08-15) — flat for everyone, founder or not, Mensual or Anual.
 * Kept as an explicit function (not inlined at call sites) so a future
 * founder-rate change has one place to land; takes `Subscription` for API
 * stability even though it's currently unused.
 */
export function modularPricePerCourt(_s: Subscription): number {
  return ModularPricing.basePerCourt;
}

/** Trial reminder threshold: returns 30, 14, 3, or null for banner coloring. */
export function trialReminderThreshold(
  s: Subscription,
  status: ClubStatus,
  now: Date = new Date(),
): number | null {
  if (status !== "trial" || s.trialEndDate == null) return null;
  const days = subscriptionTrialDaysRemaining(s, now);
  if (days <= 3) return 3;
  if (days <= 14) return 14;
  if (days <= 30) return 30;
  return null;
}

/**
 * Lifecycle milestone — one node on the timeline rendered in the
 * subscription screen. Pure data; the component picks icons + colors.
 */
export interface LifecycleMilestone {
  /** Stable key for React keys + tests. */
  key: string;
  /** Date the milestone falls on (or today, for `now`). */
  date: Date | null;
  /** Short Spanish label, e.g. "Fin de prueba". */
  label: string;
  /** Optional one-line detail, e.g. "Datos conservados 90 días". */
  detail: string | null;
  /** Position relative to "now". */
  kind: "past" | "now" | "future";
  /** Visual tone — drives left-rail color. */
  tone: "neutral" | "info" | "warn" | "bad" | "ok";
}

/**
 * The read-only → closure milestone. `graceEndDate` is never auto-computed
 * (Decision #16) — unset means read-only continues indefinitely with no
 * closure scheduled, since no automated job enforces it anyway; a platform
 * admin sets a real date by hand when they actually intend to close a club.
 */
function graceEndMilestone(
  graceEndDate: Date | null | undefined,
  now: Date,
  retention: number,
): LifecycleMilestone {
  if (!graceEndDate) {
    return {
      key: "grace-end",
      date: null,
      label: "Solo lectura",
      detail: "Sin fecha de cierre programada",
      kind: "future",
      tone: "warn",
    };
  }
  return {
    key: "grace-end",
    date: graceEndDate,
    label: "Cierre",
    detail: `Datos conservados ${retention} días`,
    kind: now > graceEndDate ? "past" : "future",
    tone: "bad",
  };
}

/**
 * Per-status timeline of what happens next. Renders the *expected* path
 * if no payment action is taken — owners need to see the consequence
 * chain ("trial ends → read-only → closure once an admin sets a date"),
 * not just the current state. Fed by the subscription's own dates
 * (trialEndDate, currentPeriodEnd, nextInvoiceDate, graceEndDate). Missing
 * dates collapse the corresponding milestone gracefully.
 */
export function lifecycleMilestones(
  status: ClubStatus,
  billingStatus: BillingStatus,
  s: Subscription,
  now: Date = new Date(),
): LifecycleMilestone[] {
  const retention = SubscriptionPricing.suspendedRetentionDays;

  // Billing override: an overdue club shows the read-only → closure chain
  // regardless of its lifecycle stage. Billing is orthogonal to lifecycle now,
  // so this guard wins over the lifecycle switch below.
  if (billingStatus === "past_due") {
    return [
      {
        key: "now",
        date: now,
        label: "Pago pendiente",
        detail: "Acceso en solo lectura hasta resolver",
        kind: "now",
        tone: "warn",
      },
      graceEndMilestone(s.graceEndDate, now, retention),
    ];
  }

  switch (status) {
    case "mockup": {
      // Pre-claim. No trial countdown yet — starts on owner accept.
      return [
        {
          key: "now",
          date: now,
          label: "Maqueta",
          detail: "Pre-lanzamiento, acceso de solo lectura",
          kind: "now",
          tone: "info",
        },
        {
          key: "claim",
          date: null,
          label: "Reclamar club",
          detail: "Inicia los 90 días de prueba al aceptar",
          kind: "future",
          tone: "neutral",
        },
      ];
    }
    case "trial": {
      const trialEnd = s.trialEndDate ?? null;
      const out: LifecycleMilestone[] = [
        {
          key: "now",
          date: now,
          label: "En prueba",
          detail: trialEnd
            ? `${subscriptionTrialDaysRemaining(s, now)} días restantes`
            : "Sin fecha de fin",
          kind: "now",
          tone: "info",
        },
      ];
      if (trialEnd) {
        const trialDone = now > trialEnd;
        out.push({
          key: "trial-end",
          date: trialEnd,
          label: "Fin de prueba",
          detail: s.paymentMethodAdded
            ? "Cobro automático del primer mes"
            : "Sin método de pago: pasa a solo lectura",
          kind: trialDone ? "past" : "future",
          tone: s.paymentMethodAdded ? "ok" : "warn",
        });
        if (!s.paymentMethodAdded) {
          out.push(graceEndMilestone(s.graceEndDate, now, retention));
        }
      }
      return out;
    }
    case "active": {
      const out: LifecycleMilestone[] = [
        {
          key: "now",
          date: now,
          label: "Activo",
          detail: s.cancelAtPeriodEnd
            ? "Cancelación programada al fin del periodo"
            : "Suscripción al día",
          kind: "now",
          tone: s.cancelAtPeriodEnd ? "warn" : "ok",
        },
      ];
      if (s.nextInvoiceDate) {
        out.push({
          key: "next-invoice",
          date: s.nextInvoiceDate,
          label: "Próximo cargo",
          detail:
            s.nextInvoiceAmount != null
              ? `€${s.nextInvoiceAmount.toLocaleString("es-ES")}`
              : null,
          kind: "future",
          tone: "info",
        });
      } else if (s.currentPeriodEnd) {
        out.push({
          key: "period-end",
          date: s.currentPeriodEnd,
          label: s.cancelAtPeriodEnd ? "Cierre" : "Renovación",
          detail: null,
          kind: "future",
          tone: s.cancelAtPeriodEnd ? "bad" : "info",
        });
      }
      return out;
    }
    case "closed": {
      return [
        {
          key: "now",
          date: now,
          label: "Cerrado",
          detail: "Sin acceso",
          kind: "now",
          tone: "bad",
        },
        {
          key: "retention-end",
          date: null,
          label: "Eliminación de datos",
          detail: `${retention} días tras el cierre`,
          kind: "future",
          tone: "neutral",
        },
      ];
    }
  }
}

/**
 * Computes the *first paid invoice* date + amount. During trial, infers
 * from `trialEndDate` and the selected plan since Stripe hasn't anchored
 * the period yet. After trial, `nextInvoiceDate` is authoritative.
 *
 * `courtCount` is the live active-court count — pass `club.activeCourtCount`
 * (cached) or `useCourts({ activeOnly: true }).data?.length` (live).
 * Stripe's `nextInvoiceAmount`, when set, always wins.
 */
export function firstPaymentEstimate(
  s: Subscription,
  courtCount: number,
  status: ClubStatus,
  now: Date = new Date(),
): { date: Date | null; amount: number } {
  const computed = courtCount * monthlyPricePerCourt(s);
  if (s.nextInvoiceDate && s.nextInvoiceDate > now) {
    return {
      date: s.nextInvoiceDate,
      amount: s.nextInvoiceAmount ?? computed,
    };
  }
  if (status === "trial" && s.trialEndDate) {
    return { date: s.trialEndDate, amount: computed };
  }
  return { date: null, amount: computed };
}

// ─── IVA helpers ───────────────────────────────────────────────────────────

/** True when the UI should show IVA breakdowns alongside net prices. */
export function shouldShowIva(s: Subscription | null | undefined): boolean {
  return !!s && s.automaticTaxEnabled === true;
}

/** Returns net + IVA. Always rounded to cents. */
export function withIva(net: number): number {
  return Math.round(net * (1 + SubscriptionPricing.ivaRate) * 100) / 100;
}

/** Just the IVA portion (gross − net). Always rounded to cents. */
export function ivaAmount(net: number): number {
  return Math.round(net * SubscriptionPricing.ivaRate * 100) / 100;
}

/**
 * Format a price for display. When `showIva` is true, returns a tuple of
 * `(headline, caption)` — e.g. `["€120", "+ €25.20 IVA · €145.20 total"]`.
 * When false, returns `(headline, null)`. Callers render the caption only
 * if non-null.
 *
 * Use `formatNetPriceCaption` when the net amount is the headline (plan
 * cards, Total mensual rows). Use `formatGrossPriceCaption` when the gross
 * amount is the headline (Próximo cargo — the actual amount Stripe will
 * charge).
 */
export function formatNetPriceCaption(net: number): string | null {
  const iva = ivaAmount(net);
  const total = withIva(net);
  return `+ €${iva.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} IVA · €${total.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} total`;
}

export function formatGrossPriceCaption(net: number): string | null {
  const iva = ivaAmount(net);
  return `€${net.toLocaleString("es-ES")} + €${iva.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} IVA`;
}

export type { ClubStatus };
